import type { CurrencyId } from "@/services/currencies/currenciesTypes";
import { CurrencyId_Schema } from "@/services/currencies/currenciesTypes";
import { z } from "zod";

const EmptyPriceValue = null;

/* Types */
export const PriceNumeric_Schema = z.object({ currencyId: CurrencyId_Schema, price: z.number() }).strict();
export type PriceNumeric = z.infer<typeof PriceNumeric_Schema>;
export const PriceEmpty_Schema = z.null();
export type PriceEmpty = z.infer<typeof PriceEmpty_Schema>;
export const PriceNumericOrEmpty_Schema = z.union([PriceNumeric_Schema, PriceEmpty_Schema]);
export type PriceNumericOrEmpty = z.infer<typeof PriceNumericOrEmpty_Schema>;

export const Price_Schema = z.union([PriceNumeric_Schema, PriceEmpty_Schema]);
export type Price = z.infer<typeof Price_Schema>;

export const PricesKeyed_Schema = z.record(z.string(), PriceNumericOrEmpty_Schema);
export type PricesKeyed = z.infer<typeof PricesKeyed_Schema>;

/* Validators */
export const isEmptyPrice = (price: unknown): price is PriceEmpty => price === EmptyPriceValue;
export const isNumericPrice = (price: unknown): price is PriceNumeric => price !== EmptyPriceValue && typeof price === "object" && price !== EmptyPriceValue && "currencyId" in price && "price" in price;
export const isNumericOrEmptyPrice = (price: unknown): price is PriceNumericOrEmpty => isEmptyPrice(price) || isNumericPrice(price);
export const assertPrice = (value: unknown): Price => {
  if (isEmptyPrice(value) || isNumericPrice(value)) {
    return value;
  } else {
    throw new InvalidPriceException();
  }
};

/* Comparators */
export const isEqualPrice = (priceA: Price, priceB: Price): boolean => {
  if (isNumericPrice(priceA) && isNumericPrice(priceB)) {
    return priceA.price === priceB.price && priceA.currencyId === priceB.currencyId;
  } else if (isEmptyPrice(priceA) && isEmptyPrice(priceB)) {
    return true;
  } else {
    return false;
  }
};

/* Factories */
export const createEmptyPrice = (): PriceEmpty => EmptyPriceValue;
export const createNumericPrice = (price: number, currencyId: CurrencyId): PriceNumeric => ({ price, currencyId });

/* Exceptions */
export class InvalidPriceException extends Error {}
